import {Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MAX_SECONDS,
  DASHBOARD_POLLING_INTERVAL_MIN_SECONDS
} from '@constants/dashboard-polling-interval.constant';
import {User} from '@models/user/user';
import {DashboardLayouts} from '@models/dashboard/dashboard-layouts';
import {Role} from '@models/user/role';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {DashboardService} from '@services/dashboard/dashboard.service';
import {Organization} from '@models/organization/organization';
import {OrganizationService} from '@services/administration/organization/organization.service';
import {MachineListUserConfigService} from '@services/dashboard/machine-list-user-config.service';
import {MachineListViewStore, MachineListViewStoreInstance} from '@services/dashboard/machine-list-view.store';

@Component({
    selector: 'scitis-machine-list-container',
    templateUrl: './machine-list-container.component.html',
    styleUrls: ['./machine-list-container.component.scss'],
    standalone: false,
    providers: [MachineListViewStore],
})
export class MachineListContainerComponent implements OnInit, OnChanges {
  @Input()
  dashboardLayout: string = '';
  @Input()
  currentLocation: string = '';
  @Input()
  currentMachineGroupId: string = '';
  @Input()
  user: User | undefined;
  @Input()
  isWindowContentVisible: boolean = true;
  @Input()
  categoriesAggregate: Map<string, MachineCategoryColumnConfiguration> = new Map<string, MachineCategoryColumnConfiguration>();

  @Output()
  public activeCategoryChange = new EventEmitter<string>();

  public isSuperAdminRole: boolean = false;
  public isAdminRole: boolean = false;
  public isAnalyticsRole: boolean = false;
  public organizationId: number = -1;
  public rootMachineryGroupId: string = '';
  public refreshInterval: number = -1;
  public whiteListedMachineryGroups: null | number[] = null;
  public userRole: Role = new Role(new User());
  public isConfigLoaded: boolean = false;

  constructor(private dashboardService: DashboardService,
              private organizationService: OrganizationService,
              private userConfigService: MachineListUserConfigService) {
  }

  public readonly store: MachineListViewStoreInstance = inject(MachineListViewStore);

  public get isListLayout(): boolean {
    return this.dashboardLayout == DashboardLayouts.LIST;
  }

  public get activeCategoryName(): string {
    return this.store.activeCategory();
  }

  public get activeCategoryConfig(): MachineCategoryColumnConfiguration | null {
    const name = this.activeCategoryName;
    if (!name) {
      return null;
    }
    return this.categoriesAggregate.get(name) ?? null;
  }

  public get activeCategoryIcon(): string {
    return this.activeCategoryConfig?.categoryIcon ?? '';
  }

  ngOnInit(): void {
    this.getUserDetails(this.user);
    this.getRefreshInterval();
    this.loadUserConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user?.currentValue) {
      this.getUserDetails(changes.user.currentValue);
      this.getRefreshInterval();
    }
    if (changes.categoriesAggregate?.currentValue && this.isConfigLoaded) {
      this.initializeActiveCategory(this.store.activeCategory());
    }
  }

  public onCategorySelect(categoryName: string): void {
    const config = this.categoriesAggregate.get(categoryName);
    if (!config) {
      return;
    }
    const allColumnNames = this.collectAllColumnNames(config);
    this.store.ensureCategoryInitialized(categoryName, allColumnNames);
    this.store.setActiveCategory(categoryName);
    this.activeCategoryChange.emit(categoryName);
  }

  private loadUserConfig(): void {
    this.userConfigService.load().then((config) => {
      this.store.hydrateFromConfig(config);
      this.isConfigLoaded = true;
      this.initializeActiveCategory(config.activeCategory);
    });
  }

  private initializeActiveCategory(savedActiveCategory: string): void {
    const availableCategoryNames = Array.from(this.categoriesAggregate.keys());
    if (availableCategoryNames.length === 0) {
      return;
    }
    const savedIsAvailable = !!savedActiveCategory && this.categoriesAggregate.has(savedActiveCategory);
    const categoryName = savedIsAvailable ? savedActiveCategory : availableCategoryNames[0];
    const config = this.categoriesAggregate.get(categoryName);
    if (!config) {
      return;
    }
    const allColumnNames = this.collectAllColumnNames(config);
    this.store.ensureCategoryInitialized(categoryName, allColumnNames);
    this.store.setActiveCategory(categoryName);
    this.activeCategoryChange.emit(categoryName);
  }

  private collectAllColumnNames(categoryConfig: MachineCategoryColumnConfiguration): string[] {
    const names: string[] = ['machineName'];
    for (const column of categoryConfig.categoryColumns ?? []) {
      if (column && column.name && column.name !== 'machineId') {
        names.push(column.name);
      }
    }
    return names;
  }

  private getUserDetails(user: User | undefined) {
    if (!user) {
      return;
    }
    const userRole = new Role(user);
    this.userRole = userRole;
    this.isSuperAdminRole = userRole.isSuperAdminRole || userRole.isTechAdminRole;
    this.isAdminRole = userRole.isAdminRole;
    this.isAnalyticsRole = userRole.isAnalyticsRole;

    this.organizationId = userRole.isSuperAdminRole ? -1 : user.parentId;
    this.rootMachineryGroupId = String(user.machineGroup);
    this.whiteListedMachineryGroups =
        user.getWhiteListedMachineryGroups(true).length == 0 || userRole.isSuperAdminRole ?  null : user.getWhiteListedMachineryGroups();
  }

  private getRefreshInterval(): void {
    const organizationId = this.user?.parentId;
    if (!organizationId || organizationId <= 0) {
      this.getGlobalRefreshInterval();
      return;
    }

    this.organizationService.getOrganizationDetails(organizationId).subscribe({
      next: (organization: Organization) => {
        const configuredInterval = Number(organization?.dashboardListPollingIntervalInSeconds);
        if (this.isValidRefreshInterval(configuredInterval)) {
          this.refreshInterval = configuredInterval;
        } else {
          this.getGlobalRefreshInterval();
        }
      },
      error: () => this.getGlobalRefreshInterval(),
    });
  }

  private isValidRefreshInterval(intervalInSeconds: number): boolean {
    return Number.isFinite(intervalInSeconds)
      && intervalInSeconds >= DASHBOARD_POLLING_INTERVAL_MIN_SECONDS
      && intervalInSeconds <= DASHBOARD_POLLING_INTERVAL_MAX_SECONDS;
  }

  private getGlobalRefreshInterval(): void {
    this.dashboardService.getDashboardListRefreshInterval().subscribe({
      next: (result) => {
        const configuredInterval = Number(result?.value);
        this.refreshInterval = this.isValidRefreshInterval(configuredInterval)
          ? configuredInterval
          : DASHBOARD_POLLING_INTERVAL_DEFAULT_SECONDS;
      },
      error: () => {
        this.refreshInterval = 10;
      }
    });
  }
}
